// DiagReadyChainPage.js — EL VISOR de la cadena de entrega.
//
// Portado desde diag/f66ap-ready-chain, donde nació para diagnosticar el bug
// que F6.7a arregla. Se queda: la tubería nueva (convergencia, coalescing,
// backoff, vigía de red) vive en momentos que jsdom NO puede simular —el
// lifecycle real de iOS, la señal que se va y vuelve, la app matada y
// relanzada— y esta pantalla es la única forma honesta de verlos. Carlos está
// en Windows: no hay inspector de Safari ni logs del iPhone.
//
// App-only (redirige en web; la ruta ni se registra allá). NO escribe ni una
// sola regla de safe area propia: se monta dentro de <AppLayout> igual que
// Ajustes o el Centro de Estudio, así que HEREDA el chasis ya probado en
// dispositivo (header fijo, separación del bottom nav, una sola zona
// scrolleable). Sin position:fixed, sin env(safe-area-inset-*), sin calc() de
// inset en este archivo. Estructura del shell copiada de SettingsPage.js
// (contenedor `max-w-2xl mx-auto space-y-8` + cabecera con botón de atrás).
//
// Botones (misma lógica que traía del diagnóstico):
//   - Copiar / Limpiar: vuelca o vacía el registro.
//   - Forzar sincronización: llama al cartero directo. DISCRIMINADOR clave — si
//     con esto el material baja, el problema estaba en el DISPARO (justo lo que
//     este sprint arregla); si no, está en la cadena.
//   - ¿Está en el teléfono?: cruza los docs vivos del servidor contra la bodega
//     y, para cada uno, dice si el material está presente Y si la convergencia
//     lo considera un hueco. El resultado se pinta DENTRO de la misma página.
//
// (Pasada final 3: los toggles de desenfoque y de aplanado 3D se retiraron —
// ambas hipótesis de la pantalla negra quedaron descartadas on-device y el
// experimento terminó. Sus bloques CSS .sb-no-blur/.sb-no-flat3d y sus códigos
// VIEWER.BLUR/VIEWER.FLAT3D se fueron con ellos.)
//
// F6.7b — REPINTADO AUTOMÁTICO cada 1 s, y SOLO mientras esta pantalla está
// montada. El registro se llenaba mientras Carlos lo miraba y la pantalla no se
// enteraba: para ver una entrada nueva había que tocar un botón o salir y
// volver, justo cuando lo que se quiere observar (la convergencia al recuperar
// la señal, el reintento del cartero) ocurre solo y sin tocar nada. Un segundo
// es suficiente para no perderse nada y el intervalo muere con el desmontaje —
// esto no es un poller de la app, es el refresco de un visor abierto.

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useAuth } from '../contexts/AuthContext';
import { getEntries, clear as clearLog, currentBootId, log as diagLog } from '../lib/diag/readyChainLog';
import api from '../lib/api';

/* TANDA 11 (firma #180) — EL MISMO CANDADO DE ADMINISTRADOR QUE EL DRAWER.
 *
 * La comparación es la de `components/Sidebar.js` y `components/routeGuards.js`,
 * y la dirección entra por variable de entorno del build, como allá. Dos avisos
 * que el letrero gemelo de `pages/SettingsPage.js` desarrolla entero:
 *
 *   1. La cadena vacía se descarta ANTES de comparar. Sin eso, un build que no
 *      definiera la variable dejaría `algo === ''`, que es verdadero para
 *      cualquier cuenta con el correo vacío: el caso degenerado fallaría
 *      ABIERTO. Hoy ninguna alta del servidor produce ese correo (los invitados
 *      también reciben uno), así que es cinturón y tirantes — pero cuesta cuatro
 *      caracteres y deja el fallo en el sentido correcto: sin dirección
 *      configurada, NADIE es administrador.
 *   2. La variable NO esconde el correo del paquete publicado (el empaquetador
 *      la sustituye por su literal); lo que evita es que viva en el código
 *      fuente del repo.
 */
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';

function fmtDetail(d) {
  if (d == null) return '';
  try {
    if (typeof d === 'string') return d;
    return JSON.stringify(d);
  } catch (e) {
    return '[unserializable]';
  }
}

export default function DiagReadyChainPage() {
  const navigate = useNavigate();
  const { user, loading: sesionCargando } = useAuth();
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState('');
  const [presence, setPresence] = useState(null); // [{id, filename, server, local, hueco}]

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  // Repintado de 1 s, acotado a esta pantalla (ver cabecera). Los botones y
  // todo lo demás siguen igual: esto solo fuerza el re-render que hace que el
  // registro se vea crecer.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const onForceSync = useCallback(async () => {
    setBusy('sync');
    diagLog('VIEWER.FORCE_SYNC', {});
    try {
      const m = await import('../lib/warehouse/downloader');
      await m.syncWarehouseDown();
    } catch (e) {
      diagLog('VIEWER.CATCH', { where: 'force-sync', msg: String((e && e.message) || e) });
    } finally {
      setBusy('');
      refresh();
    }
  }, [refresh]);

  const onCheckPhone = useCallback(async () => {
    setBusy('phone');
    diagLog('VIEWER.CHECK_PHONE', {});
    try {
      // Docs vivos del server para cruzar contra la bodega. Sin red caemos a
      // lo que haya localmente (la lista sale vacía y el bloque lo dice).
      let docs = [];
      try {
        const res = await api.get('/documents');
        docs = res.data?.documents || [];
      } catch (e) { /* sin red */ }
      const [{ readLocalPresence }, { computeConvergencePlan }] = await Promise.all([
        import('../lib/warehouse/presence'),
        import('../lib/warehouse/convergence'),
      ]);
      const local = await readLocalPresence();
      const localById = new Map(local.map((r) => [r.id, r]));
      const plan = computeConvergencePlan(docs, local, true);
      const gapById = new Map(plan.gaps.map((g) => [g.id, g.reason]));
      setPresence(docs.map((d) => ({
        id: d.id,
        filename: d.filename,
        server: d.status,
        local: localById.has(d.id),
        counts: localById.has(d.id)
          ? `${localById.get(d.id).local_question_rows}p/${localById.get(d.id).local_flashcard_rows}f`
          : '-',
        hueco: gapById.get(d.id) || null,
      })));
    } catch (e) {
      diagLog('VIEWER.CATCH', { where: 'check-phone', msg: String((e && e.message) || e) });
      setPresence([]);
    } finally {
      setBusy('');
    }
  }, []);

  // Pasada final 2 — la sonda de transporte a demanda: la MISMA petición que
  // el NET.PROBE automático de NET.UP, para medir la ventana con el dedo.
  const onProbe = useCallback(async () => {
    setBusy('probe');
    try {
      const { probeServer } = await import('../lib/diag/netProbe');
      await probeServer('viewer');
    } catch (e) {
      diagLog('VIEWER.CATCH', { where: 'probe', msg: String((e && e.message) || e) });
    } finally {
      setBusy('');
      refresh();
    }
  }, [refresh]);

  const onCopy = useCallback(async () => {
    const { entries, unreadable } = getEntries();
    const lines = entries.map((e) => `[${e.b}] +${e.m}ms ${e.w} ${e.c} ${fmtDetail(e.d)}`);
    const text = `ready-chain diag (unreadable=${unreadable}, bootActual=${currentBootId()})\n` + lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setBusy('copied');
      setTimeout(() => setBusy(''), 1200);
    } catch (e) {
      // Fallback: seleccionar un textarea oculto.
      try {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
        setBusy('copied'); setTimeout(() => setBusy(''), 1200);
      } catch (e2) { setBusy(''); }
    }
  }, []);

  const onClear = useCallback(() => {
    clearLog();
    setPresence(null);
    refresh();
  }, [refresh]);

  // App-only: en web esta ruta no se registra (App.js gatea el spread con
  // IS_APP_BUILD), pero por si acaso alguien llega aquí en web, redirige.
  if (!IS_APP_BUILD) return <Navigate to="/" replace />;

  // TANDA 11 (firma #180) — Y ADEMÁS, SÓLO EL DUEÑO ENTRA.
  //
  // La tanda le devolvió a esta pantalla una puerta visible (la tarjeta de
  // Ajustes), y una puerta con candado no basta si la pantalla de detrás abre a
  // quien teclee la dirección. Lo firmado es «ver Y entrar»: las dos mitades
  // preguntan lo mismo, con el mismo mecanismo.
  //
  // La sesión ya la resolvió `ProtectedRoute` río arriba —esta ruta va envuelta
  // en él (`App.js`)—, así que aquí no se repite el rebote a login; lo que sí se
  // repite es esperar a que la sesión termine de resolverse, porque durante ese
  // rato `user` todavía no existe y rebotar entonces echaría al propio dueño.
  // Mismo orden y misma forma que `AdminRoute` en `components/routeGuards.js`.
  if (sesionCargando) return null;
  if (!ADMIN_EMAIL || user?.email?.toLowerCase() !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  const { entries, unreadable } = getEntries();
  const boots = [];
  const byBoot = new Map();
  for (const e of entries) {
    if (!byBoot.has(e.b)) { byBoot.set(e.b, []); boots.push(e.b); }
    byBoot.get(e.b).push(e);
  }
  const curBoot = currentBootId();

  const BTN_OUTLINE = 'border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] rounded-xl h-11 text-sm';

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in-up" data-testid="diag-ready-chain-page">
      {/* Cabecera — mismo patrón que Ajustes: botón de atrás nativo + título.
          La salida también funciona con el bottom nav. */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="diag-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#6a6a6a]" />
        </Button>
        <h1 className="text-2xl font-bold text-white">Diagnóstico</h1>
      </div>

      {/* Acciones — botones normales dentro del flujo de la página, en rejilla. */}
      <div className="glass-card rounded-[20px] p-5 md:p-7 space-y-4" data-testid="diag-actions-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button onClick={onForceSync} disabled={busy === 'sync'} variant="outline"
            className={BTN_OUTLINE} data-testid="diag-force-sync-btn">
            {busy === 'sync' ? 'Sincronizando…' : 'Forzar sincronización'}
          </Button>
          <Button onClick={onCheckPhone} disabled={busy === 'phone'} variant="outline"
            className={BTN_OUTLINE} data-testid="diag-check-phone-btn">
            {busy === 'phone' ? 'Consultando…' : '¿Está en el teléfono?'}
          </Button>
          <Button onClick={onProbe} disabled={busy === 'probe'} variant="outline"
            className={BTN_OUTLINE} data-testid="diag-probe-btn">
            {busy === 'probe' ? 'Probando…' : 'Probar servidor'}
          </Button>
          <Button onClick={onCopy} variant="outline"
            className={BTN_OUTLINE} data-testid="diag-copy-btn">
            {busy === 'copied' ? '¡Copiado!' : 'Copiar'}
          </Button>
          <Button onClick={onClear} variant="outline"
            className={BTN_OUTLINE} data-testid="diag-clear-btn">
            Limpiar
          </Button>
        </div>

        {/* Resultado de "¿Está en el teléfono?" — bloque de texto DENTRO de la
            misma página, debajo de los botones (no modal, no popup). */}
        {presence && (
          <div className="border border-[#3f3f46] rounded-md p-3 bg-[#111116] font-mono text-[11px]" data-testid="diag-presence-block">
            <div className="text-[#c4b5fd] font-bold mb-1.5">¿Está en el teléfono? (ahora)</div>
            {presence.length === 0 && <div className="text-[#a1a1aa]">Sin docs / sin datos.</div>}
            {presence.map((p) => (
              <div key={p.id} className="flex justify-between gap-2 py-0.5">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {p.filename || p.id} <span className="text-[#71717a]">({p.server}, {p.counts})</span>
                </span>
                <span className={`font-bold ${p.hueco ? 'text-[#f87171]' : (p.local ? 'text-[#4ade80]' : 'text-[#a1a1aa]')}`}>
                  {p.hueco ? `HUECO: ${p.hueco}` : (p.local ? 'EN TELÉFONO' : 'NO')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Registro — agrupado por bootId (más reciente al final), monoespaciado, en
          el scroll natural de la página (el <main> de AppLayout es la única zona
          scrolleable del chasis). */}
      <div className="glass-card rounded-[20px] p-5 md:p-7 font-mono text-[11px]" data-testid="diag-log-section">
        <div className="text-[#a1a1aa] mb-2">
          {entries.length} entradas, ilegibles: {unreadable}, boot actual: <b className="text-[#c4b5fd]">{curBoot}</b>
          {tick ? '' : ''}
        </div>

        {boots.map((b) => (
          <div key={b} className="mb-3">
            <div className={`font-bold my-1.5 border-b border-dashed border-[#27272a] ${b === curBoot ? 'text-[#c4b5fd]' : 'text-[#71717a]'}`}>
              boot {b}{b === curBoot ? ' (actual)' : ''} - {byBoot.get(b).length}
            </div>
            {byBoot.get(b).map((e, i) => (
              <div key={i} className="flex gap-1.5 py-px items-baseline">
                <span className="text-[#52525b] min-w-[58px] text-right">+{e.m}ms</span>
                <span className="font-bold min-w-[92px]" style={{ color: codeColor(e.c) }}>{e.c}</span>
                <span className="text-[#d4d4d8] break-all">{fmtDetail(e.d)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function codeColor(code) {
  // Los códigos de F6.7a mandan sobre los del diagnóstico: son los que Carlos
  // busca en el teléfono durante el smoke.
  if (code === 'NET.ERR') return '#f87171';
  if (code === 'NET.PROBE') return '#60a5fa';
  if (code === 'CV.ABORT' || code.startsWith('DL.ERR') || code === 'NET.DOWN' || code.endsWith('CATCH')) return '#f87171';
  if (code === 'CV.SKIP' || code === 'DL.COALESCE') return '#fbbf24';
  if (code === 'CV.RUN' || code === 'DL.COMMIT' || code === 'DL.READY' || code === 'NET.UP') return '#4ade80';
  if (code === 'DL.RETRY' || code === 'DL.RERUN' || code === 'CV.NETUP_RETRY') return '#fb923c';
  if (code.startsWith('NL.')) return '#60a5fa';
  if (code.startsWith('DL.')) return '#c4b5fd';
  // TANDA J — la familia FLASH.* con color propio. No es adorno: el registro
  // llega a 500 entradas y el smoke de COS-33 consiste en encontrar DOS líneas
  // (FLASH.TOUCH y FLASH.SCROLL) entre todo el ruido de la cadena de entrega,
  // con el dedo y sin inspector. Misma forma que las reglas de prefijo de
  // arriba; ni una palabra nueva en pantalla.
  if (code.startsWith('FLASH.')) return '#f0abfc';
  // CARRIL-DIAG — `NAV.H1` en ROJO. Es la línea de una LEY ROTA (el carril fuera
  // de su reposo), y el smoke consiste en encontrarla entre 500 entradas con el
  // dedo y sin inspector; el resto de la familia `NAV.*` (gestos soltados) se
  // queda en el gris de siempre porque son línea normal, no alarma.
  if (code === 'NAV.H1') return '#f87171';
  return '#e5e7eb';
}
